import { createFileRoute, Link, Outlet, useMatches, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { Badge, Button, Card, Input, Spinner } from '@/components/admin/ui';
import {
  EmptyState,
} from '@/components';
import type { FlashcardSetListItem } from '@/lib/admin-types';
import { apiJson } from '@/lib/api';

function FlashcardSetsList() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'hmonglus' | 'moob'>('hmonglus');
  const [audioVoices, setAudioVoices] = useState<'dual' | 'single'>('dual');
  const [requiresSubscription, setRequiresSubscription] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'flashcard-sets'],
    queryFn: () => apiJson<FlashcardSetListItem[]>('/v1/admin/flashcard-sets'),
  });

  const createSet = useMutation({
    mutationFn: (payload: {
      name: string;
      mode: string;
      audio_voices: string;
      requires_subscription: boolean;
    }) =>
      apiJson<{ id: number }>('/v1/admin/flashcard-sets', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: (created) => {
      setName('');
      setRequiresSubscription(false);
      void qc.invalidateQueries({ queryKey: ['admin', 'flashcard-sets'] });
      void navigate({
        to: '/admin/flashcard-sets/$id',
        params: { id: String(created.id) },
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-red-600">
        {(error as Error).message}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Flashcard Sets</h1>
        <p className="mt-1 text-sm text-nova-muted-foreground">
          Create cloud-sourced flashcard sets (draft → live). Mode chooses the Learning Hub;
          audio voices is independent (dual adult+child vs single voice).
        </p>
      </div>

      <Card className="space-y-3">
        <h2 className="text-sm font-medium text-nova-foreground">New set</h2>
        <div className="flex flex-wrap items-end gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Set name (e.g. Spices)"
            className="max-w-xs"
          />
          <select
            className="rounded-lg border border-nova-input bg-nova-card h-8 px-2.5 text-sm"
            value={mode}
            onChange={(e) => {
              const next = e.target.value as 'hmonglus' | 'moob';
              setMode(next);
              setAudioVoices(next === 'moob' ? 'single' : 'dual');
            }}
          >
            <option value="hmonglus">Hmonglus</option>
            <option value="moob">Moob</option>
          </select>
          <select
            className="rounded-lg border border-nova-input bg-nova-card h-8 px-2.5 text-sm"
            value={audioVoices}
            onChange={(e) => setAudioVoices(e.target.value as 'dual' | 'single')}
          >
            <option value="dual">Dual voice (adult + child)</option>
            <option value="single">Single voice</option>
          </select>
          <label className="flex items-center gap-2 pb-2 text-sm text-nova-foreground">
            <input
              type="checkbox"
              checked={requiresSubscription}
              onChange={(e) => setRequiresSubscription(e.target.checked)}
            />
            Subscribers only
          </label>
          <Button
            disabled={!name.trim() || createSet.isPending}
            onClick={() =>
              createSet.mutate({
                name: name.trim(),
                mode,
                audio_voices: audioVoices,
                requires_subscription: requiresSubscription,
              })
            }
          >
            {createSet.isPending ? 'Creating…' : 'Create set'}
          </Button>
        </div>
        {createSet.isError ? (
          <p className="text-sm text-red-600">{(createSet.error as Error).message}</p>
        ) : null}
      </Card>

      {!data || data.length === 0 ? (
        <EmptyState title="No cloud flashcard sets yet" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((set) => (
            <Link
              key={set.id}
              to="/admin/flashcard-sets/$id"
              params={{ id: String(set.id) }}
              className={clsx(
                'group block rounded-lg focus:outline-none',
                'focus-visible:ring-2 focus-visible:ring-nova-ring focus-visible:ring-offset-2'
              )}
            >
              <Card
                className={clsx(
                  'h-full transition',
                  'group-hover:border-nova-primary/20 group-hover:shadow-sm'
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <h2 className="text-lg font-medium text-nova-foreground">{set.name}</h2>
                  <div className="flex shrink-0 flex-wrap justify-end gap-1">
                    <Badge variant={set.status === 'live' ? 'success' : 'neutral'}>
                      {set.status === 'live' ? 'Live' : 'Draft'}
                    </Badge>
                    {set.requires_subscription ? (
                      <Badge variant="warning">Premium</Badge>
                    ) : null}
                  </div>
                </div>
                <p className="text-sm text-nova-muted-foreground">{set.slug}</p>
                <p className="mt-2 text-sm text-nova-muted-foreground">
                  {set.mode} · {set.audio_voices === 'single' ? 'single voice' : 'dual voice'} ·{' '}
                  {set.card_count} card{set.card_count === 1 ? '' : 's'}
                  {set.requires_subscription ? ' · subscribers only' : ''}
                </p>
                <p className="mt-4 text-sm font-medium text-nova-primary group-hover:underline">
                  Manage set →
                </p>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/flashcard-sets')({
  component: function FlashcardSetsRoute() {
    const matches = useMatches();
    const onDetail = matches.some((m) => m.routeId.includes('flashcard-sets/$id'));
    if (onDetail) {
      return <Outlet />;
    }
    return <FlashcardSetsList />;
  },
});
